/**
 * REQ-20260510-0008 [CR-Low-L7] 登录后跳转路径决策（纯函数）
 *
 * 从 Login/index.tsx 抽出，便于单测。入参直接接收 user-like 对象，不读取
 * localStorage / 全局状态，保证函数可重入、可预测。
 *
 * 决策树（与原 Login getRedirectPath 严格保持一致，AC7 / AC10 / AC11）：
 *   1. user_scope = platform              → /platform/dashboard
 *   2. tenant scope:
 *        default_portal = admin           → /tenant/admin/dashboard
 *        default_portal = mall            → /mall (跨前端整页跳转)
 *        customer + admin portal          → /tenant/dashboard
 *        available_portals 含 admin+mall  → /tenant/dashboard
 *        仅 mall                          → /mall
 *        其余（仅 admin 或空）            → /tenant/dashboard
 */

/** 跳转决策需要的最小 user 字段（subset of UserInfo） */
export interface RedirectUserContext {
  user_scope?: string;
  available_portals?: string[];
  default_portal?: string;
  role_codes?: string[];
  roles?: Array<string | { roleCode?: string; code?: string }>;
}

/**
 * 根据用户信息决定登录后应跳转的路径。
 *
 * @param user 用户上下文。null / undefined 视为无数据，走 tenant 默认分支。
 * @returns 路径字符串（始终以 `/` 开头）
 */
export function getRedirectPath(user: RedirectUserContext | null | undefined): string {
  if (user?.user_scope === 'platform') {
    return '/platform/dashboard';
  }

  const portals = user?.available_portals ?? [];
  const defaultPortal = user?.default_portal ?? '';

  if (defaultPortal === 'admin') return '/tenant/dashboard';
  if (defaultPortal === 'mall') return '/mall';

  const hasAdmin = portals.includes('admin');
  const hasMall = portals.includes('mall');
  const roleCodes = user?.role_codes ?? (user?.roles || []).map((r) =>
    typeof r === 'string' ? r : (r.roleCode || r.code || '')
  ).filter(Boolean);

  if (roleCodes.includes('customer') && hasAdmin) return '/tenant/dashboard';
  if (hasAdmin && hasMall) return '/tenant/dashboard';
  if (hasMall && !hasAdmin) return '/mall';

  return '/tenant/dashboard';
}
